import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import type { Router } from 'vue-router';
import { EnumIsOverseas, EnumStorageKeys } from '@ais/common-shared';
import { requestPlugin } from '@ais/common-util-tools';
/** ais 公共样式 */
import '@ais-web/commons/assets/css/index.css';
import { clickOutside, loadMore } from '@ais-web/commons/directive';
import { EnumNodeEnv, EnumPermissions } from '@ais-web/commons/shared/enum';
import { handleLoginSuccess } from '@ais-web/commons/shared/hooks';
/**
 * 公共请求头处理器
 * 响应异常处理
 * */
import {
  getLocale,
  isProductEnHost,
  responseDataErrorHandler,
  responseErrorHandler,
  setAppId,
  setItem,
  setLocaleByHost,
} from '@ais-web/commons/shared/utils';
import i18n from '@L/i18n';
import { initSentry } from '@/sentry';
/** 应用视图 */
import App from './App.vue';

// 设置默认语言
if (!getLocale()) {
  setLocaleByHost();
}

// 设置海外标识
if (isProductEnHost()) {
  setItem(EnumStorageKeys.IsOverseas, EnumIsOverseas.Yes, {
    storage: sessionStorage,
  });
}

/** Vue 实例 */
const app = createApp(App);
let router: Router | undefined;

app.use(requestPlugin, {
  appId: import.meta.env.APP_ID,
  appName: import.meta.env.APP_NAME,
  appVersion: import.meta.env.APP_VERSION,
  mockURL: import.meta.env.VITE_AXIOS_MOCK_URL,
  appConfig: {
    baseURL: import.meta.env.VITE_AXIOS_BASE_URL,
  },
  Menu_id: EnumPermissions.Root,
  getMenuId: () => {
    const menuId = router?.currentRoute.value.meta.id;
    return menuId ? `${menuId}` : undefined;
  },
  responseErrorHandler,
  responseDataErrorHandler,
  responseErrorMessage: i18n.global.t('fuwuqikaixiao'),
  sentry: Sentry,
});

app.use(createPinia());

app.use(i18n);

setAppId();

window.$t = i18n.global.t;
(async () => {
  try {
    await handleLoginSuccess();
  } catch (err) {
    console.warn(err);
  } finally {
    const loadingDom = document.querySelector('#app-loading');
    if (loadingDom) {
      loadingDom.remove();
    }

    // 动态加载七牛云脚本，用于切换微软和kkview预览文件
    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        /** i18n-ignore */
        script.onerror = () => reject(new Error(`加载脚本失败:${src}`));
        document.head.appendChild(script);
      });
    };

    // 在这里添加需要加载的脚本
    try {
      await loadScript('https://oss.cninsights.com/cic-web/conf/ais-self-service.config.js');
    } catch (error) {
      console.error('七牛云脚本加载失败:', error);
    }

    const routerModule = await import('./router');
    router = routerModule.default;

    /** 插件注册 */
    app.use(routerModule.default);

    /** sentry初始化（需要在 router 注册后执行，以便正确集成路由追踪） */
    initSentry(app, routerModule.default);

    // 自定义指令注册
    app.directive('clickOutside', clickOutside);
    app.directive('loadMore', loadMore);

    if (process.env.NODE_ENV === EnumNodeEnv.Dev) {
      console.info('路由配置: ', routerModule.default.getRoutes());
    }

    app.mount('#app');
  }
})();
